import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";

/* PulseNetworkFeed — cross-portal content reader (Pulse Network Content Engine).
   Auto-hides while the engine has 0 articles for the given portalSlug.
   Snippet contract: /api/content-engine/portal-install-snippet?slug=<slug>.

   variant:
     "section" (default) — full section with heading + 3-column card grid (used on /app landing).
     "strip"             — compact horizontal strip: eyebrow title + pill cards, 1 row, scrollable.
                           Meant to sit inside a busy home page without dominating layout. */
const API_BASE = "https://kindness-hub-35.preview.emergentagent.com/api/content-engine";

export function PulseNetworkFeed({
  portalSlug = "travelerpulse",
  limit = 6,
  lang = "en",
  trustLevel = "all",
  variant = "section",
  className = "",
}) {
  const [items, setItems] = useState(null); // null = loading, [] = empty, [x…] = ready
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const url = new URL(`${API_BASE}/articles`);
        url.searchParams.set("portal_slug", portalSlug);
        url.searchParams.set("limit", String(limit));
        if (lang) url.searchParams.set("lang", lang);
        if (trustLevel && trustLevel !== "all") url.searchParams.set("trust_level", trustLevel);
        const r = await fetch(url.toString());
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) setItems(Array.isArray(data?.items) ? data.items : []);
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, [portalSlug, limit, lang, trustLevel]);

  // Snippet contract: auto-hide while empty/loading/error so sister-site pages
  // don't render a hollow section before content arrives.
  if (err || items === null || items.length === 0) return null;

  if (variant === "strip") {
    return (
      <section className={`${className}`} data-testid="pulse-network-strip">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-blue-700">Latest across the Pulse Network</div>
        </div>
        <div className="flex overflow-x-auto snap-x snap-mandatory gap-3 pb-2 scrollbar-none" style={{ scrollbarWidth: "none" }}>
          {items.slice(0, 8).map((a) => (
            <a key={a.id || a.slug || a.url} href={a.url} target="_blank" rel="noopener noreferrer"
               data-testid={`pulse-strip-item-${a.id || a.slug}`}
               className="snap-start shrink-0 w-[260px] group rounded-2xl border border-slate-200 bg-white hover:shadow-md transition-all overflow-hidden">
              {a.image_url && <img src={a.image_url} alt="" loading="lazy" className="w-full h-24 object-cover" />}
              <div className="p-3">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-700">
                  {a.source_portal_name || a.source_slug || "Pulse Network"}
                </div>
                <div className="mt-1 font-serif font-800 text-sm text-slate-900 leading-snug line-clamp-2 group-hover:text-blue-700">
                  {a.title}
                </div>
              </div>
            </a>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className={`max-w-6xl mx-auto px-5 lg:px-8 py-10 ${className}`} data-testid="pulse-network-feed">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-blue-700">From the Pulse Network</div>
          <h2 className="font-serif font-800 text-2xl sm:text-3xl text-blue-900 tracking-tight mt-1">Latest across our sister sites</h2>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((a) => (
          <a key={a.id || a.slug || a.url} href={a.url} target="_blank" rel="noopener noreferrer"
             data-testid={`pulse-feed-item-${a.id || a.slug}`}
             className="group rounded-2xl border border-slate-200 bg-white hover:shadow-md hover:-translate-y-0.5 transition-all overflow-hidden">
            {a.image_url && (
              <img src={a.image_url} alt="" loading="lazy" className="w-full h-40 object-cover" />
            )}
            <div className="p-4">
              <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-emerald-700">
                {a.source_portal_name || a.source_slug || "Pulse Network"}
              </div>
              <div className="mt-1 font-serif font-800 text-lg text-slate-900 leading-snug group-hover:text-blue-700">
                {a.title}
              </div>
              {a.summary && <p className="mt-2 text-sm text-slate-600 line-clamp-3">{a.summary}</p>}
              <div className="mt-3 text-xs text-blue-700 inline-flex items-center gap-1 font-700">
                Read <ExternalLink className="w-3 h-3" />
              </div>
            </div>
          </a>
        ))}
      </div>
    </section>
  );
}

export default PulseNetworkFeed;
